/**
 * GlassCutCheckboxCell Component
 * Checkbox component for marking orders as glass cut in the glass cutting workflow
 */

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useUpdateOrderMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import type { OrderSummary } from '../../utils/types';

interface GlassCutCheckboxCellProps {
  order: OrderSummary;
  className?: string;
  onCutChange?: (orderId: string, cut: boolean) => void;
}

export const GlassCutCheckboxCell: React.FC<GlassCutCheckboxCellProps> = ({ 
  order, 
  className = '',
  onCutChange 
}) => {
  const [updateOrder, { isLoading }] = useUpdateOrderMutation();
  const toast = useToast();
  
  // Get current glass cut status from metadata
  const currentCut = order.metadata?.glass_cutting_workflow?.cut_completed ?? false;
  const [isUpdating, setIsUpdating] = useState(false);

  const handleCutToggle = async () => {
    if (isLoading || isUpdating) return;
    
    setIsUpdating(true);
    const newCutStatus = !currentCut;
    
    try {
      // Update the metadata with glass cutting workflow status
      const updatedMetadata = {
        ...order.metadata,
        glass_cutting_workflow: {
          ...order.metadata?.glass_cutting_workflow,
          cut_completed: newCutStatus,
          cut_completed_at: newCutStatus ? new Date().toISOString() : null,
          cut_completed_by: newCutStatus ? 'current_user' : null // This would be replaced with actual user info
        }
      };
      
      await updateOrder({ 
        id: order.id, 
        data: { metadata: updatedMetadata }
      }).unwrap();
      
      toast.success(
        newCutStatus ? 'Glass Cut Complete' : 'Glass Cut Unchecked', 
        newCutStatus 
          ? 'Order marked as glass cut - print label and rack options now available' 
          : 'Glass cutting status removed'
      );
      
      // Notify parent component of the change
      onCutChange?.(order.id, newCutStatus);
      
    } catch (e: any) {
      toast.error('Update failed', e?.data?.detail || 'Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const cellClasses = [
    'p-2 border',
    'text-center',
    className
  ].filter(Boolean).join(' ');

  return (
    <td className={cellClasses}>
      <div className="flex items-center justify-center">
        <div className="flex items-center">
          <label className="relative cursor-pointer group">
            <input
              type="checkbox"
              checked={currentCut}
              onChange={handleCutToggle}
              disabled={isLoading || isUpdating}
              className="sr-only"
            />
            <div 
              className={`
                w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200
                ${currentCut 
                  ? 'bg-blue-500 border-blue-500' 
                  : 'border-gray-300 hover:border-blue-400 group-hover:bg-blue-50'
                }
                ${(isLoading || isUpdating) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {currentCut && (
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              )}
            </div>
          </label>
          {(isLoading || isUpdating) && (
            <div className="ml-2 w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
          )}
        </div>
      </div>
    </td>
  );
};

GlassCutCheckboxCell.displayName = 'GlassCutCheckboxCell';
